import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { SailingOutlined } from '@mui/icons-material';
import {
  ACTIVE_PROJECT_STATUS_LABELS,
  ActiveProjectStatus,
  CommercialDashboard,
  formatDate,
} from '../../utils/commercialDashboard';
import { ConsolePanel, EmptyNote, PanelStat, StatusPill, useConsoleTableSx } from './ConsolePanel';
import { useConsolePalette } from './consolePalette';

const STATUS_TONE: Record<ActiveProjectStatus, 'sand' | 'slate' | 'starting'> = {
  in_progress: 'sand',
  on_hold: 'slate',
  starting: 'starting',
};

type ActiveProjectsPanelProps = {
  dashboard: CommercialDashboard;
  onOpenProject: (projectId: number) => void;
};

const ActiveProjectsPanel: React.FC<ActiveProjectsPanelProps> = ({ dashboard, onOpenProject }) => {
  const palette = useConsolePalette();
  const tableSx = useConsoleTableSx();
  const { rows, count } = dashboard.projects;

  return (
    <ConsolePanel
      title="Active projects"
      subtitle="Client projects in execution or on Hold"
      icon={<SailingOutlined />}
      action={<PanelStat label="Projects" value={count} />}
      bodySx={{ p: 0, display: 'flex', flexDirection: 'column' }}
    >
      {rows.length === 0 ? (
        <Box sx={{ p: 2, flex: 1 }}>
          <EmptyNote>No client project is On-going or on Hold for this selection.</EmptyNote>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 284, overflowY: 'auto' }}>
          <Table size="small" stickyHeader sx={tableSx} aria-label="Active projects">
            <TableHead>
              <TableRow>
                <TableCell>Project / Client</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>End</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const pastEnd = row.status !== 'on_hold' && row.end_date != null && row.end_date < dashboard.today;
                return (
                  <TableRow
                    key={row.project_id}
                    hover
                    tabIndex={0}
                    onClick={() => onOpenProject(row.project_id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') onOpenProject(row.project_id);
                    }}
                  >
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography noWrap sx={{ fontSize: '0.8rem', fontWeight: 600, color: palette.ink }}>
                        {[row.nd_number, row.name].filter(Boolean).join(' · ') || 'Untitled project'}
                      </Typography>
                      <Typography noWrap variant="caption" component="div" sx={{ color: palette.inkFaint }}>
                        {row.client_name || 'No client'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: `${palette.inkSoft} !important`, whiteSpace: 'nowrap' }}>
                      {row.project_type || '—'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.start_date)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: pastEnd ? `${palette.fall} !important` : undefined }}>
                      {pastEnd ? (
                        <Tooltip title="Past its end date and still open">
                          <span>{formatDate(row.end_date)}</span>
                        </Tooltip>
                      ) : formatDate(row.end_date)}
                    </TableCell>
                    <TableCell>
                      <StatusPill label={ACTIVE_PROJECT_STATUS_LABELS[row.status]} tone={STATUS_TONE[row.status]} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </ConsolePanel>
  );
};

export default ActiveProjectsPanel;
